import React, { useEffect, useRef } from 'react';
import { Volume2, Users, Fingerprint, Heart, Sparkles } from 'lucide-react';
import './AboutPage.css';

const AboutPage = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    if (pageRef.current) {
      const fadeElements = pageRef.current.querySelectorAll('.fade-in');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { threshold: 0.1 });

      fadeElements.forEach(el => observer.observe(el));
      return () => observer.disconnect();
    }
  }, []);

  return (
    <div className="about-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-bg"></div>
        <div className="about-hero-overlay"></div>
        <div className="container about-hero-content">
          <h1 className="fade-in">About Anyone Music Records</h1>
          <p className="fade-in">Music Lives In Everyone</p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-mission">
        <div className="container">
          <div className="mission-content fade-in">
            <h2>Our Mission</h2>
            <p className="mission-tagline">Music Lives In Everyone</p>
            <p>
              We believe music isn't reserved for the chosen few. It's in all of us — waiting to be heard, to be shared, to be celebrated.
            </p>
            <p>
              Anyone Music Records exists to turn that belief into reality. We're building a label where artists are supported at every stage — from the first beat they create in their bedroom to the moment they step onto an international stage. We provide mentorship, industry connections, label releases, and most importantly — a community that believes in what you're building.
            </p>
            <p>
              This isn't about gatekeeping. It's about opening doors. It's about proving that the distance between dreaming about a music career and living one isn't as far as you think.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="container">
          <h2 className="section-title fade-in">What We Stand For</h2>
          
          <div className="values-grid">
            <div className="value-card fade-in">
              <div className="value-icon">
                <Volume2 size={32} />
              </div>
              <h3>Amplification</h3>
              <p>Everyone deserves to be heard</p>
            </div>

            <div className="value-card fade-in">
              <div className="value-icon">
                <Users size={32} />
              </div>
              <h3>Community</h3>
              <p>We build together, we grow together</p>
            </div>

            <div className="value-card fade-in">
              <div className="value-icon">
                <Fingerprint size={32} />
              </div>
              <h3>Authenticity</h3>
              <p>Real stories, real sound, real connection</p>
            </div>

            <div className="value-card fade-in">
              <div className="value-icon">
                <Heart size={32} />
              </div>
              <h3>Support</h3>
              <p>Artists are people, not products</p>
            </div>

            <div className="value-card fade-in">
              <div className="value-icon">
                <Sparkles size={32} />
              </div>
              <h3>Opportunity</h3>
              <p>From bedroom to mainstage is possible</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="about-team">
        <div className="container">
          <h2 className="section-title fade-in">Meet the Team</h2>
          
          <div className="team-grid">
            {/* Nico Aguilera */}
            <div className="team-card fade-in" data-testid="team-nico">
              <div className="team-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5330895f-a43f-4c6a-8cc1-5aee89bfa455/artifacts/axb60xm5_Screenshot%202026-03-04%20at%2011.17.58%E2%80%AFPM.png" 
                  alt="Nico Aguilera - Anyone"
                />
              </div>
              <div className="team-info">
                <span className="team-role">Founder / Producer / Mentor</span>
                <h3>Nico Aguilera</h3>
                <span className="team-alias">Anyone</span>
                <p className="team-tagline">"Some sounds find you before you find them."</p>
                <div className="team-bio">
                  <p>
                    Nicolás José Aguilera — known artistically as Anyone — is a music producer and DJ with Argentinian and Moroccan roots, currently based in Ibiza. His musical journey began at 16, drawn first into the raw pulse of hip hop before the deeper gravity of electronic music took hold.
                  </p>
                  <p>
                    Those early years shaping his craft in local scenes led to a defining collaboration with the Belgian progressive house label AH Digital, carrying his sound across stages in Argentina, Chile, and Brazil. In 2022, driven by a desire to push his artistic vision further, Anyone relocated to Tulum — where he emerged as one of the region's most compelling talents.
                  </p>
                  <p>
                    That same passion for music led Anyone to found Anyone Music Productions — a mentorship platform that has helped 200+ producers and DJs develop their sound, secure releases on respected international labels, and perform at festivals across Tulum, Barcelona, and Ibiza.
                  </p>
                  <p className="team-closing">
                    The sound is organic. The journey is real. The movement is Anyone.
                  </p>
                </div>
              </div>
            </div>

            {/* George Barakat */}
            <div className="team-card fade-in" data-testid="team-george">
              <div className="team-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5330895f-a43f-4c6a-8cc1-5aee89bfa455/artifacts/7bl0taw2_IMG_2866.jpeg" 
                  alt="George Barakat"
                />
              </div>
              <div className="team-info">
                <span className="team-role">Co-Founder / Progressive Techno Artist</span>
                <h3>George Barakat</h3>
                <p className="team-tagline">"Some people wait for the right moment. George Barakat decided the moment was now."</p>
                <div className="team-bio">
                  <p>
                    George Barakat is a progressive techno artist, DJ, and co-founder of Anyone Music Records. Father. Husband. Entrepreneur. And someone who has felt the pull of the beat his entire life.
                  </p>
                  <p>
                    For years, the music lived inside him — a constant rhythm running beneath the demands of business, family, and everything in between. It was his children who finally made it undeniable. Watching them dive headfirst into music with fearless creativity and raw enthusiasm, George didn't just feel inspired — he felt called.
                  </p>
                  <p>
                    George crafts emotionally driven, mainstage-ready music built for peak-time moments and immersive dancefloor experiences. His sound moves between driving grooves, cinematic tension, and expansive melodies — music designed not just to fill a room, but to own it.
                  </p>
                  <p className="team-closing">
                    One track. One artist. One unforgettable moment at a time.
                  </p>
                </div>
              </div>
            </div>

            {/* Gerry Sullivan */}
            <div className="team-card fade-in" data-testid="team-gerry">
              <div className="team-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_af294ea2-fcee-46fc-a14a-c351fc5fce15/artifacts/o6hk6o3k_IMG_0016.jpeg" 
                  alt="Gerry Sullivan"
                />
              </div>
              <div className="team-info">
                <span className="team-role">Co-Founder / Marketing Strategist</span>
                <h3>Gerry Sullivan</h3>
                <p className="team-tagline">"Some people find music. Gerry Sullivan was born into it."</p>
                <div className="team-bio">
                  <p>
                    Raised in Newfoundland & Labrador, Gerry grew up steeped in the Irish musical traditions that pulse through the island's soul — where music isn't entertainment, it's identity. From kitchen parties to community halls, song and sound were the backdrop to his earliest years.
                  </p>
                  <p>
                    That passion showed up early on local stages across NL, where Gerry quickly discovered something just as powerful as the music itself — the community it builds. He turned that belief into action, founding an events company that placed live music at the heart of community life.
                  </p>
                  <p>
                    Over a career that has taken him across industries and boardrooms, one constant has followed Gerry everywhere: a deep understanding of what makes people connect. As a Marketing Strategist, he has crafted signature personal brands and developed bold, results-driven strategies for corporations and institutions alike.
                  </p>
                  <p className="team-closing">
                    The music was always calling. Now, he's all in.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invitation Section */}
      <section className="about-story">
        <div className="container">
          <div className="story-content fade-in">
            <p className="invitation-text">
              Whether you're an aspiring producer, an established DJ, or someone who just loves music and wants to be part of something real — this is your invitation.
            </p>
            <blockquote>
              Music lives in everyone. Let's turn yours up.
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <h2 className="fade-in">Ready to Join the Movement?</h2>
          <p className="fade-in">Whether you're an artist looking for a home or a fan ready to discover your next favorite track, we want to hear from you.</p>
          <div className="cta-buttons fade-in">
            <a href="/benefits" className="btn btn-primary">Submit a Demo</a>
            <a href="/contact" className="btn btn-secondary">Get in Touch</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
